import { COMMERCIAL_STATUS_META } from './leadsConfig';

/**
 * Resumen comercial por registro (API `include=commercial_summary`).
 * La API manda un bloque mínimo para registros sin actividad; acá se
 * completan los defaults para que el resto del Front no asuma claves.
 * @see docs/frontend/commercial-summary.md
 */

const num = (v) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v) : 0);
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

function normalizeOffers(raw) {
  const o = obj(raw);
  const pick = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
  return { USD: pick(o.USD), ARS: pick(o.ARS) };
}

function normalizeBase(r) {
  return {
    status: COMMERCIAL_STATUS_META[r.status] ? r.status : 'sin_interes',
    by_stage: obj(r.by_stage),
    interests_count: num(r.interests_count),
    leads_count: num(r.leads_count),
    inquiries_count: num(r.inquiries_count),
    unseen_inquiries_count: num(r.unseen_inquiries_count),
    offers: normalizeOffers(r.offers),
    last_activity_at: r.last_activity_at || null,
  };
}

/** @returns {object|null} null si la API no lo mandó (sin include). */
export function normalizeCommercialSummary(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const mismatch = raw.stock_mismatch === true ? true : (raw.stock_mismatch === null ? null : false);
  return {
    ...normalizeBase(raw),
    stock_status: raw.stock_status ?? null,
    stock_mismatch: mismatch,
    stock_mismatch_reason: mismatch ? (raw.stock_mismatch_reason || null) : null,
  };
}

/** @returns {object|null} */
export function normalizeCommercialRollup(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const unitsByStatus = obj(raw.units_by_status);
  const unitsSum = Object.values(unitsByStatus).reduce((a, b) => a + num(b), 0);
  return {
    ...normalizeBase(raw),
    units_by_status: unitsByStatus,
    units_count: raw.units_count != null ? num(raw.units_count) : unitsSum,
    units_stock_mismatch_count: num(raw.units_stock_mismatch_count),
    includes: Array.isArray(raw.includes) ? raw.includes : ['cadastral_lot', 'user_lot'],
  };
}

/** Normaliza in place una fila de getmylots (lote + units[]). */
export function normalizeLotCommercial(lot) {
  if (!lot || typeof lot !== 'object') return lot;
  const next = { ...lot };
  if ('commercial_summary' in lot) next.commercial_summary = normalizeCommercialSummary(lot.commercial_summary);
  if ('commercial_rollup' in lot) next.commercial_rollup = normalizeCommercialRollup(lot.commercial_rollup);
  if (Array.isArray(lot.units)) {
    next.units = lot.units.map((u) => (u && 'commercial_summary' in u
      ? { ...u, commercial_summary: normalizeCommercialSummary(u.commercial_summary) }
      : u));
  }
  return next;
}

export function normalizeCadParcelCommercial(parcel) {
  if (!parcel || typeof parcel !== 'object' || !('commercial_summary' in parcel)) return parcel;
  return { ...parcel, commercial_summary: normalizeCommercialSummary(parcel.commercial_summary) };
}

export function getCommercialStatusMeta(status) {
  return COMMERCIAL_STATUS_META[status] || COMMERCIAL_STATUS_META.sin_interes;
}

/** ¿Hay algo para mostrar? `sin_interes` sin mismatch no lleva indicador. */
export function hasCommercialSignal(s) {
  if (!s) return false;
  return s.status !== 'sin_interes'
    || s.stock_mismatch === true
    || num(s.units_stock_mismatch_count) > 0
    || num(s.unseen_inquiries_count) > 0;
}

const STATUS_ORDER = ['cerrado', 'reservado', 'ofertado', 'en_seguimiento', 'contactado', 'nuevo', 'con_consultas', 'descartado', 'sin_interes'];

const fmtAmount = (n) => new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(n);

export function formatOffers(offers) {
  const o = normalizeOffers(offers);
  return ['USD', 'ARS'].filter((c) => o[c] != null).map((c) => `${c} ${fmtAmount(o[c])}`).join(' / ');
}

export function formatRelativeActivity(iso, now = Date.now()) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const days = Math.floor((now - t) / 86400000);
  if (days <= 0) return 'hoy';
  if (days === 1) return 'ayer';
  if (days < 30) return `hace ${days} d`;
  return new Date(t).toLocaleDateString('es-AR');
}

export const STOCK_MISMATCH_LABELS = {
  stock_ahead: 'Stock más avanzado que los intereses (vendido o reservado por fuera, o lead archivado)',
  interest_ahead: 'Hay un interés reservado o cerrado y el stock no lo refleja',
};

/**
 * Desglose sin colores de un lote con UF: registros por etapa (UF de
 * `units_by_status` + la etapa propia del lote si tiene interés directo), en
 * minúscula y género neutro: '1 contactado · 3 sin interés'. '' si no hay UF.
 */
export function formatRollupBreakdown(rollup, lotSummary = null) {
  if (!rollup || !(num(rollup.units_count) > 0)) return '';
  const counts = { ...obj(rollup.units_by_status) };
  if (lotSummary && lotSummary.status && lotSummary.status !== 'sin_interes') {
    counts[lotSummary.status] = num(counts[lotSummary.status]) + 1;
  }
  const keys = [...STATUS_ORDER, ...Object.keys(counts).filter((k) => !STATUS_ORDER.includes(k))];
  return keys
    .filter((k) => num(counts[k]) > 0)
    .map((k) => `${num(counts[k])} ${getCommercialStatusMeta(k).label.toLocaleLowerCase('es')}`)
    .join(' · ');
}

/** Líneas del tooltip. `kind`: 'summary' | 'rollup'. Rollup con UF: desglose, no la etapa. */
export function buildCommercialTooltipLines(s, { kind = 'summary', now, lotSummary = null } = {}) {
  if (!s) return [];
  const breakdown = kind === 'rollup' ? formatRollupBreakdown(s, lotSummary) : '';
  const lines = [breakdown ? `Lote y UF: ${breakdown}` : getCommercialStatusMeta(s.status).label];
  const counts = [];
  if (s.leads_count > 0) counts.push(`${s.leads_count} lead${s.leads_count === 1 ? '' : 's'}`);
  if (s.inquiries_count > 0) {
    const unseen = s.unseen_inquiries_count > 0 ? ` (${s.unseen_inquiries_count} sin leer)` : '';
    counts.push(`${s.inquiries_count} consulta${s.inquiries_count === 1 ? '' : 's'}${unseen}`);
  }
  if (counts.length) lines.push(counts.join(' · '));
  const offers = formatOffers(s.offers);
  if (offers) lines.push(`Oferta máx.: ${offers}`);
  const rel = formatRelativeActivity(s.last_activity_at, now);
  if (rel) lines.push(`Última actividad: ${rel}`);
  if (kind === 'rollup' && s.units_stock_mismatch_count > 0) {
    const n = s.units_stock_mismatch_count;
    lines.push(`${n} UF con stock que no coincide con los intereses`);
  }
  if (kind === 'summary' && s.stock_mismatch === true) {
    lines.push(STOCK_MISMATCH_LABELS[s.stock_mismatch_reason] || 'El stock no coincide con los intereses');
  }
  return lines;
}

/** Mismatch visible (⚠). `stock_ahead` en CAD va solo en el tooltip (stock manual). */
export function showMismatchMark(s, { kind = 'summary', isCad = false } = {}) {
  if (!s) return false;
  if (kind === 'rollup') return s.units_stock_mismatch_count > 0;
  if (s.stock_mismatch !== true) return false;
  return !(isCad && s.stock_mismatch_reason === 'stock_ahead');
}

/* Refresco: cambiar etapa de interés / marcar consultas vistas. */
export const COMMERCIAL_CHANGED_EVENT = 'terrio:commercial-changed';

export function emitCommercialChanged(detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(COMMERCIAL_CHANGED_EVENT, { detail }));
}

export function onCommercialChanged(handler) {
  if (typeof window === 'undefined') return () => {};
  const fn = (e) => handler(e.detail || {});
  window.addEventListener(COMMERCIAL_CHANGED_EVENT, fn);
  return () => window.removeEventListener(COMMERCIAL_CHANGED_EVENT, fn);
}

/* ── Detalle / ficha (paso 6) ─────────────────────────────────────────── */

const unitKey = (u) => String(u?.id || u?.user_lot_id || '');

/**
 * Bloques comerciales de `getuserlotunits?include=commercial_summary`.
 * @returns {{ lot: object|null, rollup: object|null, units: Record<string, object> }|null}
 *   null si la respuesta no trae ninguna clave (sin include / cálculo falló).
 */
export function extractUnitsCommercial(json) {
  if (!json || typeof json !== 'object') return null;
  const rows = Array.isArray(json.data) ? json.data : [];
  const units = {};
  rows.forEach((u) => {
    if (u && u.commercial_summary && unitKey(u)) {
      units[unitKey(u)] = normalizeCommercialSummary(u.commercial_summary);
    }
  });
  const lot = normalizeCommercialSummary(json.lot_commercial_summary);
  const rollup = normalizeCommercialRollup(json.commercial_rollup);
  if (!lot && !rollup && Object.keys(units).length === 0) return null;
  return { lot, rollup, units };
}

/** Pega `commercial_summary` en las filas de UF que no lo traen (no pisa el resto). */
export function attachUnitsCommercial(units, commercial) {
  if (!Array.isArray(units) || !commercial || !commercial.units) return units;
  let changed = false;
  const next = units.map((u) => {
    const s = commercial.units[unitKey(u)];
    if (!s || u.commercial_summary === s) return u;
    changed = true;
    return { ...u, commercial_summary: s };
  });
  return changed ? next : units;
}

/** Normaliza `data.parcels.features[].properties.commercial_summary` (getcadproject). */
export function normalizeCadProjectCommercial(project) {
  const features = project?.parcels?.features;
  if (!Array.isArray(features)) return project;
  if (!features.some((f) => f?.properties && 'commercial_summary' in f.properties)) return project;
  return {
    ...project,
    parcels: {
      ...project.parcels,
      features: features.map((f) => (f?.properties && 'commercial_summary' in f.properties
        ? { ...f, properties: { ...f.properties, commercial_summary: normalizeCommercialSummary(f.properties.commercial_summary) } }
        : f)),
    },
  };
}

/** `{ parcelId: summary }` de un proyecto CAD (solo parcelas que lo traen). */
export function indexCadProjectCommercial(project, getId) {
  const out = {};
  (project?.parcels?.features || []).forEach((f) => {
    const id = getId(f);
    const s = f?.properties?.commercial_summary;
    if (id && s) out[String(id)] = s;
  });
  return out;
}

/**
 * Datos de la línea de rollup arriba del listado de UF (`CommercialRollupDetails`). null → no se muestra nada
 * (sin bloque, o `sin_interes` sin señales: igual que las cards).
 * `mismatch` es texto secundario; nunca reemplaza al status.
 */
export function buildCommercialDetailView(s, { kind = 'summary', isCad = false, now, lotSummary = null } = {}) {
  if (!hasCommercialSignal(s)) return null;
  const meta = getCommercialStatusMeta(s.status);
  const details = [];
  if (kind === 'rollup') {
    // Mismo desglose que la línea de las cards (lote + UF por etapa).
    const breakdown = formatRollupBreakdown(s, lotSummary);
    if (breakdown) details.push(breakdown);
  }
  if (s.leads_count > 0) details.push(`${s.leads_count} lead${s.leads_count === 1 ? '' : 's'}`);
  if (s.inquiries_count > 0) {
    const unseen = s.unseen_inquiries_count > 0 ? ` (${s.unseen_inquiries_count} sin leer)` : '';
    details.push(`${s.inquiries_count} consulta${s.inquiries_count === 1 ? '' : 's'}${unseen}`);
  }
  const offers = formatOffers(s.offers);
  if (offers) details.push(`Oferta máx.: ${offers}`);
  const rel = formatRelativeActivity(s.last_activity_at, now);
  if (rel) details.push(`Última actividad: ${rel}`);
  let mismatch = null;
  if (kind === 'rollup' && s.units_stock_mismatch_count > 0) {
    mismatch = `${s.units_stock_mismatch_count} UF con stock que no coincide con los intereses`;
  } else if (kind === 'summary' && s.stock_mismatch === true) {
    // En CAD `stock_ahead` también se muestra acá (texto secundario, no ⚠).
    mismatch = STOCK_MISMATCH_LABELS[s.stock_mismatch_reason] || 'El stock no coincide con los intereses';
  }
  return {
    status: s.status,
    label: meta.label,
    color: meta.color || null,
    details,
    mismatch,
    mismatchMark: showMismatchMark(s, { kind, isCad }),
  };
}

/**
 * Texto compacto para la línea de las cards (ciudad · resumen). Mismos datos
 * que el tooltip, abreviados. null → solo la ciudad.
 * @returns {{ status, label, color, parts: string[], unseen: number, mismatchMark: boolean }|null}
 */
export function buildCommercialInlineParts(s, { kind = 'summary', isCad = false, hideUnseen = false, now, lotSummary = null } = {}) {
  if (!hasCommercialSignal(s)) return null;
  const meta = getCommercialStatusMeta(s.status);
  // Lote con UF: sin punto ni etapa (parecía el estado del lote); desglose en texto.
  const breakdown = kind === 'rollup' ? formatRollupBreakdown(s, lotSummary) : '';
  const parts = breakdown ? [breakdown] : [];
  if (s.leads_count > 0) parts.push(`${s.leads_count} lead${s.leads_count === 1 ? '' : 's'}`);
  if (s.inquiries_count > 0) parts.push(`${s.inquiries_count} consulta${s.inquiries_count === 1 ? '' : 's'}`);
  const rel = formatRelativeActivity(s.last_activity_at, now);
  if (rel) parts.push(rel);
  return {
    status: s.status,
    label: breakdown ? null : meta.label,
    color: breakdown ? null : (meta.color || null),
    breakdown: Boolean(breakdown),
    parts,
    unseen: hideUnseen ? 0 : num(s.unseen_inquiries_count),
    mismatchMark: showMismatchMark(s, { kind, isCad }),
  };
}

/* ── Detalle de lista (getsharedlist / getmylots?list_id con include) ──── */

const lotKey = (l) => String(l?.lot_id || '');

/**
 * Normaliza `include=commercial_summary` de getsharedlist (API 41f597b):
 * grouped → `lots[].commercial_summary` / `commercial_rollup` / `units[].commercial_summary`;
 * flat → `lots[].commercial_summary` (UF si `item_type: 'unit'`, lote catastral si 'lot').
 */
export function normalizeSharedListCommercial(data) {
  if (!data || !Array.isArray(data.lots)) return data;
  return {
    ...data,
    lots: data.lots.map((row) => {
      if (!row || typeof row !== 'object') return row;
      if (Array.isArray(row.units)) return normalizeLotCommercial(row);
      return 'commercial_summary' in row
        ? { ...row, commercial_summary: normalizeCommercialSummary(row.commercial_summary) }
        : row;
    }),
  };
}

/** Índice `{ lots: {lotId: {summary?, rollup?}}, units: {ufId: summary} }` desde lotes grouped o flat. */
function indexListCommercial(sourceLots) {
  const lots = {};
  const units = {};
  (Array.isArray(sourceLots) ? sourceLots : []).forEach((row) => {
    if (!row || typeof row !== 'object') return;
    if (Array.isArray(row.units)) {
      const entry = {};
      if (row.commercial_summary) entry.summary = row.commercial_summary;
      if (row.commercial_rollup) entry.rollup = row.commercial_rollup;
      if (Object.keys(entry).length && lotKey(row)) lots[lotKey(row)] = entry;
      row.units.forEach((u) => {
        if (u?.commercial_summary && unitKey(u)) units[unitKey(u)] = u.commercial_summary;
      });
      return;
    }
    if (!row.commercial_summary) return;
    if (row.item_type === 'unit' && unitKey(row)) units[unitKey(row)] = row.commercial_summary;
    else if (lotKey(row)) lots[lotKey(row)] = { ...(lots[lotKey(row)] || {}), summary: row.commercial_summary };
  });
  return { lots, units };
}

/**
 * Pega los bloques comerciales de `sourceLots` en `groupedLots` (por `lot_id` y
 * id de UF) sin tocar el resto. Lo que no viene en la fuente se conserva (si el
 * cálculo falló, queda el último). Devuelve el mismo array si no cambia nada,
 * para no re-renderizar (refresco tras `terrio:commercial-changed` sin flicker).
 */
export function mergeListCommercial(groupedLots, sourceLots) {
  if (!Array.isArray(groupedLots)) return groupedLots;
  const idx = indexListCommercial(sourceLots);
  if (!Object.keys(idx.lots).length && !Object.keys(idx.units).length) return groupedLots;
  let changed = false;
  const next = groupedLots.map((lot) => {
    const entry = idx.lots[lotKey(lot)];
    let out = lot;
    if (entry?.summary && entry.summary !== lot.commercial_summary) out = { ...out, commercial_summary: entry.summary };
    if (entry?.rollup && entry.rollup !== lot.commercial_rollup) out = { ...out, commercial_rollup: entry.rollup };
    if (Array.isArray(lot.units)) {
      const units = attachUnitsCommercial(lot.units, { units: idx.units });
      if (units !== lot.units) out = { ...out, units };
    }
    if (out !== lot) changed = true;
    return out;
  });
  return changed ? next : groupedLots;
}
